type TilledPaymentMethodBillingDetails = {
  name: string
  email: string
  phone?: string
  address: {
    city?: string
    country?: string
    state?: string
    street?: string
    street2?: string
    zip: string
  }
}

type TilledPaymentMethodCard = {
  number: string
  exp_month: number
  exp_year: number
  cvc: string
}

export type CreateTilledPaymentMethodParams = {
  publishableKey: string
  cardAccountId: string
  billingDetails: TilledPaymentMethodBillingDetails
  card: TilledPaymentMethodCard
}

export type TilledPaymentMethod = {
  id: string
  type: "card"
}

type TilledErrorResponse = {
  statusCode?: number
  message?: string | string[]
  error?: string
}

const tilledFieldLabels: Record<string, string> = {
  "card.number": "card number",
  "card.exp_month": "expiration month",
  "card.exp_year": "expiration year",
  "card.cvc": "CVC",
  "billing_details.name": "billing name",
  "billing_details.email": "email address",
  "billing_details.phone": "phone number",
  "billing_details.address.city": "billing city",
  "billing_details.address.state": "billing state",
  "billing_details.address.street": "street address",
  "billing_details.address.street2": "address line 2",
  "billing_details.address.zip": "billing ZIP code",
}

const exactValidationMessages: Record<string, string> = {
  "card.number must be a credit card": "Please enter a valid card number.",
  "card.exp_month must be an integer number": "Please enter a valid expiration month.",
  "card.exp_year must be an integer number": "Please enter a valid expiration year.",
  "card.cvc should not be empty": "Please enter a valid CVC.",
}

const tilledApiBaseUrl =
  process.env.ENVIRONMENT === "dev" || process.env.ENVIRONMENT === "development"
    ? "https://sandbox-api.tilled.com"
    : "https://api.tilled.com"

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function withPeriod(value: string) {
  return value.endsWith(".") ? value : `${value}.`
}

function parseTilledErrorMessages(error: unknown) {
  const rawMessage =
    error instanceof Error ? error.message : typeof error === "string" ? error : undefined

  if (!rawMessage) {
    return []
  }

  try {
    const parsedError = JSON.parse(rawMessage) as TilledErrorResponse
    const { message } = parsedError

    if (Array.isArray(message)) {
      return message.filter((item): item is string => typeof item === "string")
    }

    if (typeof message === "string") {
      return [message]
    }
  } catch {
    return [rawMessage]
  }

  return []
}

function formatValidationRule(rule: string) {
  return rule
    .replace(/^must be a credit card$/i, "must be valid")
    .replace(/^must be an email$/i, "must be a valid email address")
    .replace(/^must be an integer number$/i, "must be valid")
    .replace(/^should not be empty$/i, "is required")
}

function formatTilledValidationMessage(message: string) {
  const normalizedMessage = message.trim().replace(/\.$/, "")
  const exactMessage = exactValidationMessages[normalizedMessage.toLowerCase()]

  if (exactMessage) {
    return exactMessage
  }

  const fieldPath = Object.keys(tilledFieldLabels).find((path) =>
    normalizedMessage.toLowerCase().startsWith(`${path.toLowerCase()} `)
  )

  if (!fieldPath) {
    return withPeriod(normalizedMessage)
  }

  const rule = normalizedMessage.slice(fieldPath.length).trim()
  return withPeriod(`${capitalize(tilledFieldLabels[fieldPath])} ${formatValidationRule(rule)}`)
}

export function getTilledPaymentMethodErrorMessage(error: unknown) {
  const messages = parseTilledErrorMessages(error)

  if (!messages.length) {
    return null
  }

  const formattedMessages = messages.map(formatTilledValidationMessage)

  if (formattedMessages.length === 1) {
    return formattedMessages[0]
  }

  return `Please check the following card details:\n${formattedMessages
    .map((message) => `- ${message}`)
    .join("\n")}`
}

export async function createTilledPaymentMethod({
  publishableKey,
  cardAccountId,
  billingDetails,
  card,
}: CreateTilledPaymentMethodParams): Promise<TilledPaymentMethod> {
  const response = await fetch(`${tilledApiBaseUrl}/v1/payment-methods`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${publishableKey}`,
      "tilled-account": cardAccountId,
    },
    body: JSON.stringify({
      type: "card",
      billing_details: billingDetails,
      card,
    }),
  })

  if (response.status < 200 || response.status >= 300) {
    const responseText = await response.text()
    throw new Error(responseText || "Unable to create payment method")
  }

  return await response.json()
}
